import * as React from 'react';
import Button from '@mui/material/Button';
import { Avatar, Box, Card, CardActions, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Pagination, Select, Snackbar, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography, debounce } from '@mui/material';
import { addUserEffects, approveAccountDeletionRequest, assignUserIdToDeletionRequest, cancelOffer, changeAdsStatusForUser, getAccountDeletionRequests, getBetlistsPaginated, getFunnelList, getOffers, getPromoBanners, getTemplates, getUserAggregatedInfo, getUsersPaginated, rejectAccountDeletionRequest, updateBetStateForBetlist, updateUserBalance } from '../Api';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PromoBannerDto from '../models/dtos/PromoBannerDto';
import PromoBannerCard from '../components/PromoBannerCard';
import TemplateDto from '../models/dtos/TemplateDto';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import FunnelDto from '../models/dtos/FunnelDto';
import FunnelConfigDto from '../models/dtos/FunnelConfigDto';
import { GridCellParams, GridValueGetterParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { useCallback } from 'react';
import AccountDeletionRequestDto from '../models/dtos/AccountDeletionRequestDto';
import { rowSelectionStateInitializer } from '@mui/x-data-grid/internals';
import AccountMainInfoDto, { LastSubscriptionDto } from '../models/dtos/AccountMainInfoDto';
import { UserTierStatus } from '../components/SubscriptionTierChip';
import AggregateUserInfoDto from '../models/dtos/AggregateUserInfoDto';
import { userPurposeLabel } from '../models/userPurpose';
import BetlistShortDto from '../models/dtos/BetlistShortDto';
import BetlistsTable from '../components/BetlistsTable';
import BalanceTable from '../components/BalanceTable';
import SupportChatTable from '../components/SupportChatTable';
import VideoRewardsTable from '../components/VideoRewardsTable';
import UserOffersTable from '../components/UserOffersTable';
import UserEventsTable from '../components/UserEventsTable';
import UserGamesTable from '../components/UserGamesTable';
import UserDailyQuestsTable from '../components/UserDailyQuestsTable';
import UserBattlesTable from '../components/UserBattlesTable';
import UserQuizzesTable from '../components/UserQuizzesTable';
import UserPrivateClubsTable from '../components/UserPrivateClubsTable';
import UserAchievementsTable from '../components/UserAchievementsTable';
import UserMissionsTable from '../components/UserMissionsTable';
import UserFeedPostsTab from '../components/betupFeed/UserFeedPostsTab';
import UserViolationsTab from '../components/betupFeed/UserViolationsTab';
import UserDailyBonusTab from '../components/UserDailyBonusTab';
import UserPremiumTab from '../components/UserPremiumTab';
import UserPurchasesTab from '../components/UserPurchasesTab';

/** The user page's tabs in order; `key` is the `?tab=` value links use. */
const USER_TABS: { key: string; label: string }[] = [
    { key: 'bets', label: 'Betlists' },
    { key: 'balance', label: 'Balance history' },
    { key: 'chat', label: 'Chat with the user' },
    { key: 'purchases', label: 'Purchases' },
    { key: 'premium', label: 'Premium' },
    { key: 'videoRewards', label: 'Video Rewards' },
    { key: 'offers', label: 'Offers' },
    { key: 'games', label: 'Games' },
    { key: 'events', label: 'Events' },
    { key: 'dailyQuests', label: 'Daily quests' },
    { key: 'dailyBonus', label: 'Daily bonus' },
    { key: 'battles', label: 'Battles' },
    { key: 'quizzes', label: 'Quizzes' },
    { key: 'privateClubs', label: 'Private Clubs' },
    { key: 'achievements', label: 'Achievements' },
    { key: 'missions', label: 'Missions' },
    { key: 'feedPosts', label: 'Feed posts' },
    { key: 'violations', label: 'Violation reports' },
];


export default function UserPage() {
    let userId = parseInt(useParams()['id'] ?? '0');

    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    // `?tab=purchaseRequests` (older links) opens Purchases on its requests subtab.
    const tabKey = tabParam === 'purchaseRequests' ? 'purchases' : tabParam;
    const initialTab = Math.max(0, USER_TABS.findIndex(tab => tab.key === tabKey));

    const [state, setState] = React.useState({
        toastMsg: '' as string | null,
        tabIndex: initialTab,
        loaded: false,
        userId: userId,
        user: {} as AggregateUserInfoDto,
        basicInfo: { id: userId, email: '', osType: 'android', createdAt: '', purpose: null as string | null, purposeUpdatedAt: null as string | null, accountProvider: 'unknown', registeredBy: null as string | null, subscriptionTier: 'FREE', lastSubscription: null as LastSubscriptionDto | null, countryName: '', countryPhotoUrl: '', experience: 0, appVersion: '', timezone: '', adsEnabled: true, level: 0, lang: '', totalMoneyWon: 0, purchasesCount: 0, totalPurchased: 0, videosWatched: 0 },
        chatId: searchParams.get('chatId') ? parseInt(searchParams.get('chatId')!) : null,
        balanceInput: '' as string,
        addToBalanceInput: '' as string,
        balanceBusy: false,
    });

    const parseBalanceAmount = (raw: string): number | null => {
        const cleaned = raw.trim().replace(/[,\s_]/g, '');
        if (!cleaned) return null;
        if (!/^\d+$/.test(cleaned)) return null;
        const value = Number(cleaned);
        if (!Number.isFinite(value) || value > Number.MAX_SAFE_INTEGER) return null;
        return Math.trunc(value);
    };

    React.useEffect(() => {
        if (!state.loaded || state.user?.balance?.moneyInfo == null) return;
        setState(prev => ({
            ...prev,
            balanceInput: String(prev.user.balance.moneyInfo ?? ''),
        }));
    }, [state.loaded, state.userId, state.user?.balance?.moneyInfo]);

    const navigate = useNavigate();

    const onUserClicked = async (userId: number) => {
        // todo implement
    }

    const onMessageClicked = async (userId: number) => {
        // todo implement
    }

    const onBalanceUpdateClicked = async () => {
        const balance = parseBalanceAmount(state.balanceInput);
        if (balance == null) {
            alert("Enter a valid whole number balance (0 … " + Number.MAX_SAFE_INTEGER.toLocaleString() + ")");
            return;
        }

        setState(prev => ({ ...prev, balanceBusy: true }));
        const result = await updateUserBalance(state.userId, balance);
        setState(prevState => ({
            ...prevState,
            balanceBusy: false,
            toastMsg: result ? "Balance was updated!" : "Something went wrong!",
            user: result
                ? { ...prevState.user, balance: { ...prevState.user.balance, moneyInfo: balance } }
                : prevState.user,
            balanceInput: result ? String(balance) : prevState.balanceInput,
        }));
    }

    const onAddToBalanceClicked = async () => {
        const amountToAdd = parseBalanceAmount(state.addToBalanceInput);
        if (amountToAdd == null || amountToAdd <= 0) {
            alert("Enter a positive whole number to add");
            return;
        }
        const current = Number(state.user?.balance?.moneyInfo ?? 0);
        const next = current + amountToAdd;
        if (!Number.isFinite(next) || next > Number.MAX_SAFE_INTEGER) {
            alert("Resulting balance is too large");
            return;
        }

        setState(prev => ({ ...prev, balanceBusy: true }));
        const result = await updateUserBalance(state.userId, next);
        setState(prevState => ({
            ...prevState,
            balanceBusy: false,
            toastMsg: result
                ? `Added ${amountToAdd.toLocaleString()} — new balance ${next.toLocaleString()}`
                : "Something went wrong!",
            user: result
                ? { ...prevState.user, balance: { ...prevState.user.balance, moneyInfo: next } }
                : prevState.user,
            balanceInput: result ? String(next) : prevState.balanceInput,
            addToBalanceInput: result ? '' : prevState.addToBalanceInput,
        }));
    }

    const onAdsChanged = async (value: boolean) => {
        let result = await changeAdsStatusForUser(state.userId, value);
        if (result)
            setState(prevState => ({ ...prevState, user: { ...prevState.user, advanced: { ...prevState.user.advanced, adsEnabled: value } } }));
    }

    const onAddEffectsClicked = async () => {
        let result = await addUserEffects(state.userId);
        setState(prevState => ({ ...prevState, toastMsg: result != null ? "Added " + result + " effects to the user!" : "Something went wrong!" }));
    }

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'id', width: 128 },
        { field: 'email', headerName: 'email', width: 250 },
        {
            field: 'createdAt', headerName: 'CreatedAt', width: 200, valueGetter: (params: GridValueGetterParams) =>
                new Date(params.row.createdAt).toLocaleString()
        },
        {
            field: 'countryName', headerName: 'Country', width: 150, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row.countryName}&nbsp;<img src={params.row.countryPhotoUrl} width={24} height={24} /></React.Fragment>
        },
        {
            field: 'purpose', headerName: 'Purpose', width: 230, renderCell: (params: GridCellParams) =>
                <Tooltip title={params.row.purposeUpdatedAt ? 'Answered ' + new Date(params.row.purposeUpdatedAt).toLocaleString() : 'The player has not answered "Why do you play?"'}>
                    <span style={{ color: params.row.purpose ? undefined : 'gray' }}>{userPurposeLabel(params.row.purpose)}</span>
                </Tooltip>
        },
        {
            field: 'accountProvider', headerName: 'Account', width: 120, renderCell: (params: GridCellParams) =>
                <Tooltip title={params.row.registeredBy ? 'registeredBy: ' + params.row.registeredBy : 'registeredBy is empty'}>
                    <Chip size="small" label={params.row.accountProvider} variant={params.row.accountProvider === 'guest' ? 'filled' : 'outlined'} color={params.row.accountProvider === 'guest' ? 'warning' : 'default'} />
                </Tooltip>
        },
        {
            field: 'subscriptionTier', headerName: 'Tier', width: 170, renderCell: (params: GridCellParams) =>
                <UserTierStatus tier={params.row.subscriptionTier} last={params.row.lastSubscription} />
        },
        { field: 'timezone', headerName: 'Timezone', width: 150 },
        { field: 'experience', headerName: 'Experience', width: 150, },
        { field: 'level', headerName: 'Level', width: 60 },
        { field: 'lang', headerName: 'Lang', width: 60 },
        { field: 'purchasesCount', headerName: 'Purchases', width: 100 },
        {
            field: 'totalPurchased', headerName: 'Total Purchased', width: 150, valueGetter: (params: GridValueGetterParams) =>
                `$${params.row.totalPurchased.toFixed(2)}`
        },
        { field: 'videosWatched', headerName: 'Videos Watched', width: 150 },
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setState(prevState => ({ ...prevState, tabIndex: newValue }));
    };

    const fetchData = async (userId: number) => {
        window.localStorage.setItem('webGames.playAsUserId', String(userId));
        setState(prevState => ({ ...prevState, loaded: false }));
        let [userInfo] = await Promise.all([getUserAggregatedInfo(userId)]);
        let basicInfo = {
            id: userId,
            email: userInfo?.general.email ?? '', osType: userInfo?.general.osType ?? '', createdAt: userInfo?.general.createdAt ?? '', purpose: userInfo?.general.purpose ?? null, purposeUpdatedAt: userInfo?.general.purposeUpdatedAt ?? null, accountProvider: userInfo?.general.accountProvider ?? 'unknown', registeredBy: userInfo?.general.registeredBy ?? null, subscriptionTier: userInfo?.general.subscriptionTier ?? 'FREE', lastSubscription: userInfo?.general.lastSubscription ?? null, countryName: userInfo?.advanced.countryName ?? '', countryPhotoUrl: userInfo?.advanced.countryPhotoUrl ?? '',
            timezone: userInfo?.advanced.timezone ?? '', experience: userInfo?.balance.experience ?? 0, appVersion: userInfo?.advanced.appVersion ?? '', adsEnabled: userInfo?.advanced.adsEnabled ?? true, level: userInfo?.balance.level ?? 0, lang: userInfo?.advanced.lang ?? '', totalMoneyWon: userInfo?.ranks.totalMoneyWon ?? 0,
            purchasesCount: userInfo?.activity.purchasesCount ?? 0, totalPurchased: userInfo?.activity.totalPurchased ?? 0, videosWatched: userInfo?.activity.videosWatched ?? 0
        };
        setState(prevState => ({ ...prevState, user: userInfo ?? { general: { name: 'Not found' } } as AggregateUserInfoDto, loaded: userInfo != null, basicInfo: basicInfo}));
    }

    const getTabForTabIndex = (index: number) => {
        switch (USER_TABS[index]?.key) {
            case 'balance': return <BalanceTable userId={state.userId} limit={500} />;
            case 'chat': return <SupportChatTable userId={state.userId} chatId={state.chatId} />;
            case 'purchases': return <UserPurchasesTab userId={state.userId}
                initialSubtab={tabParam === 'purchaseRequests' ? 'requests' : 'purchases'} />;
            case 'premium': return <UserPremiumTab userId={state.userId} />;
            case 'videoRewards': return <VideoRewardsTable userId={state.userId} />;
            case 'offers': return <UserOffersTable userId={state.userId} userLang={state.user?.advanced?.lang} />;
            case 'games': return <UserGamesTable userId={state.userId} userLang={state.user?.advanced?.lang} />;
            case 'events': return <UserEventsTable userId={state.userId} />;
            case 'dailyQuests': return <UserDailyQuestsTable userId={state.userId} />;
            case 'dailyBonus': return <UserDailyBonusTab userId={state.userId} />;
            case 'battles': return <UserBattlesTable userId={state.userId} />;
            case 'quizzes': return <UserQuizzesTable userId={state.userId} />;
            case 'privateClubs': return <UserPrivateClubsTable userId={state.userId} user={state.user?.general} />;
            case 'achievements': return <UserAchievementsTable userId={state.userId} />;
            case 'missions': return <UserMissionsTable userId={state.userId} />;
            case 'feedPosts': return <UserFeedPostsTab userId={state.userId} />;
            case 'violations': return <UserViolationsTab userId={state.userId} />;
            default: return <BetlistsTable userId={state.userId} />;
        }
    }

    React.useEffect(() => {
        fetchData(state.userId);
    }, []);

    return (
        <div>
            <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
                <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 6 }} alignItems='center'>
                    <Grid item xs={0} sm={1} md={3} lg={5} />
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button variant="contained" fullWidth={true} style={{ height: '54px' }} onClick={() => navigate('/users')}>Go to Users</Button>
                    </Grid>
                    <Box width="100%" />
                    <Grid item xs={0} sm={0} md={1} lg={2} />
                    <Grid item xs={1} sm={1} md={2} lg={2}>
                        <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center' direction='row'>
                            <Avatar
                                alt={state.user?.general?.name}
                                src={state.user?.general?.photoUrl}
                                sx={{ width: 72, height: 72 }}
                            />
                            <Typography variant="h3">{state.loaded ? state.user.general.name : "Loading user info..."}</Typography>
                        </Stack>
                    </Grid>
                    <Box width="100%" />
                    {!state.loaded ?
                        <React.Fragment>
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item xs={1} sm={2} md={2} lg={2} key="progress">
                                <Stack alignContent='center' alignItems="center" direction="row" sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <CircularProgress size={64} style={{ marginBottom: '32px' }} /></Stack></Grid>

                        </React.Fragment> : <React.Fragment>
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item lg={6} xs={1} sm={2} md={4}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>OS Type</TableCell>
                                            <TableCell>Build Version</TableCell>
                                            <TableCell>Build Number</TableCell>
                                            <TableCell>Legacy App Version</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>
                                                {state.user?.deviceInfo?.osType ? (
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                        <img
                                                            src={state.user.deviceInfo.osType.includes('android') ? '/android_logo.png' : '/ios_logo.png'}
                                                            width={24}
                                                            height={24}
                                                            alt={state.user.deviceInfo.osType}
                                                        />
                                                        <Typography variant="body2">
                                                            {state.user.deviceInfo.osType}
                                                        </Typography>
                                                    </Box>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell>{state.user?.deviceInfo?.buildVersion ?? '—'}</TableCell>
                                            <TableCell>{state.user?.deviceInfo?.buildNumber ?? '—'}</TableCell>
                                            <TableCell>{state.user?.advanced?.appVersion ?? '—'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Grid>
                            <Box width="100%" />
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item lg={6} xs={1} sm={2} md={4}>
                                <DataGrid sx={{ width: '100%' }}
                                    rows={[state.basicInfo]}
                                    columns={columns}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { page: 0, pageSize: 25 },
                                        },
                                    }}
                                    pageSizeOptions={[5, 10]}
                                    hideFooterPagination
                                    hideFooter
                                />
                            </Grid>
                            <Box width="100%" />
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item lg={6} xs={1} sm={2} md={4}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width="75%">Tags</TableCell>
                                            <TableCell>Last time active at</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>
                                                <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ py: 0.5 }}>
                                                    {(state.user?.userTagsInfo?.tags ?? []).length > 0 ? (
                                                        (state.user?.userTagsInfo?.tags ?? []).map(tag => (
                                                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">No tags</Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                {state.user?.userTagsInfo?.lastActiveAt != null
                                                    ? new Date(state.user.userTagsInfo.lastActiveAt).toLocaleString()
                                                    : '—'}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Grid>
                            <Box width="100%" />
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                <TextField
                                    label="Money balance"
                                    variant="filled"
                                    value={state.balanceInput}
                                    fullWidth={true}
                                    inputMode="numeric"
                                    disabled={state.balanceBusy}
                                    onChange={e => setState(prev => ({ ...prev, balanceInput: e.target.value }))}
                                />
                            </Grid>
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                <Button
                                    variant="contained"
                                    fullWidth={true}
                                    style={{ height: '54px' }}
                                    disabled={state.balanceBusy}
                                    onClick={() => onBalanceUpdateClicked()}
                                >
                                    Update balance
                                </Button>
                            </Grid>
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                <TextField
                                    label="Add to balance"
                                    variant="filled"
                                    value={state.addToBalanceInput}
                                    fullWidth={true}
                                    inputMode="numeric"
                                    disabled={state.balanceBusy}
                                    helperText="Amount to add (not final total)"
                                    onChange={e => setState(prev => ({ ...prev, addToBalanceInput: e.target.value }))}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') onAddToBalanceClicked();
                                    }}
                                />
                            </Grid>
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    fullWidth={true}
                                    style={{ height: '54px' }}
                                    disabled={state.balanceBusy}
                                    onClick={() => onAddToBalanceClicked()}
                                >
                                    Add
                                </Button>
                            </Grid>
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                <Button variant={state.user.advanced.adsEnabled ? 'outlined' : 'contained'} fullWidth={true} style={{ height: '54px' }} onClick={() => onAdsChanged(!state.user.advanced.adsEnabled)}>{state.user.advanced.adsEnabled ? 'Disable ads' : 'Enable ads'}</Button>
                            </Grid>
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                <Button variant="contained" fullWidth={true} style={{ height: '54px' }} onClick={() => onAddEffectsClicked()}>Add effects</Button>
                            </Grid>
                            <Box width="100%" />
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item lg={6} xs={1} sm={2} md={4}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs value={state.tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                                        {USER_TABS.map(tab => <Tab key={tab.key} label={tab.label} />)}
                                    </Tabs>
                                </Box>
                            </Grid>
                            <Box width="100%" />
                            <Grid item xs={0} sm={0} md={1} lg={2} />
                            <Grid item lg={6} xs={1} sm={2} md={4}>
                                {
                                    getTabForTabIndex(state.tabIndex)
                                }
                            </Grid>
                        </React.Fragment>}
                </Grid>
            </Stack>
            <Snackbar
                open={state.toastMsg != null}
                autoHideDuration={1500}
                message={state.toastMsg}
                onClose={() => { setState(prevState => ({ ...prevState, toastMsg: null })) }}
            />
        </div >
    );
}
